import React from 'react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { TabNavigation } from '../../../components/operations/TabNavigation';
import { OperationsSwitcher } from '../../../components/operations/OperationsSwitcher';
import { Phone, Mail, MapPin, Wrench, AlertCircle, Plus } from 'lucide-react';
const TEAM_TABS = [{
  label: 'Shift Planner',
  path: ''
}, {
  label: 'Leave Timeline',
  path: 'leave'
}, {
  label: 'Team Directory',
  path: 'directory'
}, {
  label: 'Performance',
  path: 'performance'
}, {
  label: 'Costs',
  path: 'costs'
}];
const MEMBERS = [{
  name: 'Marko Stankovic',
  role: 'Shift Leader',
  type: 'Full-time',
  since: 'Mar 2023',
  status: 'on_shift',
  location: 'Vračar',
  skills: ['Grill', 'Burgers', 'Pancakes'],
  phone: '+381 61 123 4567',
  email: 'marko@collina.rs',
  emergency: 'Ana +381 60...',
  image: 'bg-blue-500'
}, {
  name: 'Ana Petrovic',
  role: 'Kitchen Staff',
  type: 'Part-time',
  since: 'Jun 2023',
  status: 'off',
  location: 'Banovo Brdo',
  skills: ['Salads', 'Prep'],
  phone: '+381 61 987 6543',
  email: 'ana@collina.rs',
  emergency: 'Mom +381 60...',
  image: 'bg-pink-500'
}, {
  name: 'Stefan Milic',
  role: 'Grill Master',
  type: 'Full-time',
  since: 'Jan 2022',
  status: 'on_leave',
  location: 'Novi Beograd',
  skills: ['Grill', 'Meat Prep'],
  phone: '+381 61 555 4444',
  email: 'stefan@collina.rs',
  emergency: 'Wife +381 60...',
  image: 'bg-purple-500'
}, {
  name: 'Nuran',
  role: 'Kitchen Staff',
  type: 'Full-time',
  since: 'Aug 2023',
  status: 'starting_soon',
  location: 'Vračar',
  skills: ['Pancakes', 'Cleaning'],
  phone: '+381 61 222 3333',
  email: 'nuran@collina.rs',
  emergency: 'Brother +381 60...',
  image: 'bg-orange-500'
}];
function StatusBadge({
  status
}: {
  status: string;
}) {
  const styles = {
    on_shift: {
      color: 'text-accent-success',
      label: 'On Shift',
      dot: 'bg-accent-success'
    },
    off: {
      color: 'text-text-secondary',
      label: 'Off Today',
      dot: 'bg-text-secondary'
    },
    on_leave: {
      color: 'text-accent-error',
      label: 'On Leave',
      dot: 'bg-accent-error'
    },
    starting_soon: {
      color: 'text-accent-warning',
      label: 'Starting Soon',
      dot: 'bg-accent-warning'
    }
  };
  const style = styles[status as keyof typeof styles];
  return <div className={`flex items-center gap-1.5 text-xs font-medium ${style.color}`} data-id="element-2408">
      <div className={`w-2 h-2 rounded-full ${style.dot}`} data-id="element-2409"></div>
      {style.label}
    </div>;
}
export function TeamDirectory() {
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-2410">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-2411">
        <h1 className="text-2xl font-bold text-text-primary" data-id="element-2412">Team Directory</h1>
        <Button icon={<Plus size={16} data-id="element-2414" />} data-id="element-2413">Add Employee</Button>
      </div>

      <OperationsSwitcher data-id="element-2415" />

      <TabNavigation tabs={TEAM_TABS} basePath="/operations/team" data-id="element-2416" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2417">
        {MEMBERS.map((member, i) => <Card key={i} className="flex flex-col h-full" data-id="element-2418">
            <div className="flex items-start gap-4 mb-6" data-id="element-2419">
              <div className={`w-16 h-16 rounded-full ${member.image} flex items-center justify-center text-white text-xl font-bold shrink-0`} data-id="element-2420">
                {member.name.charAt(0)}
              </div>
              <div data-id="element-2421">
                <h3 className="font-bold text-lg text-text-primary" data-id="element-2422">
                  {member.name}
                </h3>
                <div className="text-sm text-text-secondary" data-id="element-2423">
                  {member.role} · {member.type}
                </div>
                <div className="text-xs text-text-muted mt-1" data-id="element-2424">
                  Since {member.since}
                </div>
                <div className="mt-2" data-id="element-2425">
                  <StatusBadge status={member.status} data-id="element-2426" />
                </div>
              </div>
            </div>

            <div className="space-y-3 flex-1" data-id="element-2427">
              <div className="flex items-center gap-3 text-sm" data-id="element-2428">
                <MapPin size={16} className="text-text-secondary shrink-0" data-id="element-2429" />
                <span data-id="element-2430">{member.location}</span>
              </div>
              <div className="flex items-center gap-3 text-sm" data-id="element-2431">
                <Wrench size={16} className="text-text-secondary shrink-0" data-id="element-2432" />
                <span className="text-text-secondary" data-id="element-2433">
                  {member.skills.join(', ')}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm" data-id="element-2434">
                <Phone size={16} className="text-text-secondary shrink-0" data-id="element-2435" />
                <span data-id="element-2436">{member.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-sm" data-id="element-2437">
                <Mail size={16} className="text-text-secondary shrink-0" data-id="element-2438" />
                <a href={`mailto:${member.email}`} className="hover:text-accent-primary transition-colors" data-id="element-2439">
                  {member.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-accent-warning" data-id="element-2440">
                <AlertCircle size={16} className="shrink-0" data-id="element-2441" />
                <span data-id="element-2442">Emergency: {member.emergency}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6 pt-4 border-t border-white/5" data-id="element-2443">
              <Button variant="secondary" size="sm" data-id="element-2444">
                Edit
              </Button>
              <Button size="sm" data-id="element-2445">Schedule</Button>
            </div>
          </Card>)}
      </div>
    </div>;
}